import React, { useCallback, useMemo } from 'react'
import { SyncStatusView } from '../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/models'
import './IntegrationSavedModalBody.scss'
import { Button } from '../../Button/Button'
import classNames from 'classnames'
import { ActionSuggestionItem } from '../../ActionSuggestion/ActionSuggestionItem'
import { IntegrationServiceId } from '../../../common/integrations'
import { useAppSelector } from '../../../store/hooks'
import { selectCurrentUser } from '../../../store/meetingSummary/user'
import { useActionSuggestions } from '../../ActionSuggestion/hooks/hooks'
import { IntegrationSavedIconCombo } from '../IntegrationSavedIconCombo/IntegrationSavedIconCombo'
import { IntegrationSavedHistoryEntry } from '../IntegrationSavedHistoryEntry/IntegrationSavedHistoryEntry'
import { useConsolidateTasksSyncedTogether } from '../useConsolidateTasksSyncedTogether'
import { useSyncStatuses } from '../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/hooks'
import { getExternalLink } from '../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

interface IntegrationSavedModalBodyProps {
  syncStatuses: SyncStatusView[]
  serviceId: string
  syncTime?: Date | null
  onHide: () => void
  fullWidth?: boolean
}

export const IntegrationSavedModalBody: React.FC<IntegrationSavedModalBodyProps> = ({ syncStatuses, syncTime, serviceId, onHide, fullWidth = false }) => {
  const currentUser = useAppSelector(selectCurrentUser)

  const newSyncStatuses = useMemo(() => {
    return syncStatuses.filter((status) => !syncTime || syncTime < new Date(status.syncedAt))
  }, [syncTime, syncStatuses])

  const hasNewSync = newSyncStatuses.length > 0

  const { suggestions } = useActionSuggestions()
  const { syncStatuses: allSyncStatuses } = useSyncStatuses()
  const consolidatedSyncStatuses = useConsolidateTasksSyncedTogether(allSyncStatuses)

  const { trackingClient } = useTracking()

  const onClickOpenExternalLink = useCallback(
    (serviceId: string) =>
      trackingClient.track({
        eventName: TrackEvent.CRM_LINK_CLICKED,
        eventDetails: {
          serviceId: serviceId,
          source: 'workflow',
        },
      }),
    [trackingClient],
  )

  const getTitle = () => {
    if (!hasNewSync) {
      return (
        <>
          Saved to <span className='text-capitalize'>{serviceId}</span>
        </>
      )
    }

    const status = newSyncStatuses[0]
    if (newSyncStatuses.length === 1) {
      switch (status?.type) {
        case 'export':
          return (
            <div>
              The 'Meeting' was successfully saved to <span className='text-capitalize'>{serviceId}</span>
            </div>
          )
        case 'meetingSummary':
          return (
            <div>
              The 'Meeting summary' was successfully saved to <span className='text-capitalize'>{serviceId}</span>
            </div>
          )
        case 'aiResult':
          return (
            <div>
              The '{status.name}' was successfully saved to <span className='text-capitalize'>{serviceId}</span>
            </div>
          )
        case 'task':
          return (
            <div>
              Saved 'Task' to <span className='text-capitalize'>{serviceId}</span>
            </div>
          )
      }
    }

    switch (status?.type) {
      case 'task':
        return (
          <div>
            Successfully saved {newSyncStatuses.length} tasks to <span className='text-capitalize'>{serviceId}</span>
          </div>
        )
      default:
        return (
          <div>
            Successfully saved {newSyncStatuses.length} items to <span className='text-capitalize'>{serviceId}</span>
          </div>
        )
    }
  }

  const externalLink = getExternalLink({ syncStatus: newSyncStatuses[0], currentUser })

  return (
    <div className='integration-saved-modal p-4 d-flex flex-column bg-white rounded-3 shadow-sm' style={{ height: '40rem' }}>
      <div className='integration-saved-modal__content d-flex gap-3 flex-grow-1' style={{ height: '100%' }}>
        <div
          className={classNames(
            'integration-saved-modal__left',
            'flex-grow-1',
            'bg-light',
            'rounded-3',
            'p-4',
            'text-center',
            'd-flex',
            'flex-column',
            'align-items-center',
            'justify-content-center',
            { 'full-width': fullWidth },
          )}
        >
          <div className='integration-saved-modal__icons'>
            <IntegrationSavedIconCombo serviceId={serviceId as IntegrationServiceId} />
          </div>

          <h3 className='integration-saved-modal__title'>{getTitle()}</h3>

          {hasNewSync && externalLink && (
            <a
              href={externalLink}
              target='_blank'
              rel='noopener noreferrer'
              className='integration-saved-modal__link'
              onClick={() => onClickOpenExternalLink(serviceId)}
            >
              Open in {serviceId}
            </a>
          )}
        </div>

        <div className='integration-saved-modal__right w-40 d-flex flex-column' style={{ height: '100%' }}>
          <div className='integration-saved-modal__section d-flex flex-column flex-grow-1'>
            <h4 className='integration-saved-modal__section-title'>Updated external systems</h4>
            <div className='integration-saved-modal__scrollable flex-grow-1'>
              {consolidatedSyncStatuses.length > 0 ? (
                consolidatedSyncStatuses.map((syncStatus, index) => (
                  <React.Fragment key={`${syncStatus.type}-${syncStatus.serviceId}-${syncStatus.syncedAt}-${syncStatus.externalId}`}>
                    <div className='my-4'>
                      <IntegrationSavedHistoryEntry syncStatus={syncStatus} />
                    </div>
                    {index < consolidatedSyncStatuses.length - 1 && <hr className='zocks-bg-color-gray-200 m-0' />}
                  </React.Fragment>
                ))
              ) : (
                <div className='text-muted text-center my-4'>No updates.</div>
              )}
            </div>
          </div>

          <div className='integration-saved-modal__section d-flex flex-column flex-grow-1'>
            <h4 className='integration-saved-modal__section-title'>Recommended next steps</h4>
            <div className='integration-saved-modal__scrollable flex-grow-1'>
              <div className='integration-saved-modal__buttons'>
                {suggestions.map((suggestion) => (
                  <ActionSuggestionItem key={`${suggestion.actionType}-${suggestion.serviceId}`} actionSuggestion={suggestion} onClick={onHide} />
                ))}
              </div>
            </div>
          </div>

          <div className='integration-saved-modal__footer mt-3 d-flex justify-content-end'>
            <Button variant='primary' onClick={onHide}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
